import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'accent' | 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost';
type Size = 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}

/**
 * Weight follows consequence (`design-system`, "Weight of red follows
 * consequence"; the table is in `src/index.css`).
 *
 * `danger-ghost` mirrors `ghost` exactly, one token swapped, so it introduces no
 * new idea: **red at rest**, tinted on hover. Not red-only-on-hover — a control
 * that looks affirmative until the pointer arrives is unusable on a touch screen
 * and invisible to anyone scanning the row. It is the *trigger*; the solid
 * `danger` fill below is the *confirmation*, and belongs in a dialog and nowhere
 * else.
 *
 * **No variant fades.** `danger-ghost` never did — `#b91c1c` at half opacity on
 * `--color-surface` lands near 2.5:1, a washed pink that reads as decoration on
 * exactly the control that is trying to teach a rule — and the contrast audit in
 * `src/index.css` found the other four were no better: `opacity-50` blends the
 * label *and* the fill toward the page, costing the primary button 2.16:1 and
 * the ghost button 2.12:1. `design-system`, "Every colour pairing in use meets a
 * stated contrast floor", requires the floor in every state a person can reach,
 * **including unavailable**, so all five now go grey and legible instead:
 * a muted ground with soft ink where there was a fill, muted ink where there was
 * none. Grey is also more honest — a disabled destructive control is not
 * promising a destructive act it will not perform.
 */
const DISABLED_FILLED = 'disabled:bg-surface-muted disabled:text-ink-soft';
const DISABLED_GHOST = 'disabled:text-ink-muted disabled:hover:bg-transparent';

/**
 * `accent` is the design's neon, and it exists for **one control**: the one that
 * starts today's session. It is the only place in the product a client needs to
 * be pulled towards, and the hi-fi paints exactly it.
 *
 * Two things about it are requirements rather than styling, and both are in the
 * contrast table in `src/index.css`:
 *
 *   * the label is `on-accent`, a near-black, at 10.52:1. The neon is a **fill**;
 *     it is 1.59:1 on the page and can never be a text colour;
 *   * it carries a `primary` border. A #00DD8E fill is 1.59:1 against the page,
 *     so the fill alone is not a visible boundary, and 1.4.11 asks 3:1 of
 *     whatever identifies a control. The border measures 7.34:1 against the page
 *     and 4.62:1 against its own fill. Without it the button's edge is a guess.
 *
 * The border stays `primary` when the control is unavailable, where it measures
 * 6.34:1 against the muted ground every filled variant falls back to — so the
 * button does not lose its edge in the one state it is hardest to read.
 */
const VARIANTS: Record<Variant, string> = {
  accent: `bg-accent text-on-accent border border-primary hover:bg-accent-strong ${DISABLED_FILLED}`,
  primary: `bg-primary text-on-primary hover:bg-primary-strong ${DISABLED_FILLED}`,
  secondary: `bg-surface-raised text-ink border border-border-strong hover:bg-surface-muted ${DISABLED_FILLED}`,
  ghost: `bg-transparent text-primary hover:bg-primary-tint ${DISABLED_GHOST}`,
  danger: `bg-danger text-on-danger hover:brightness-90 ${DISABLED_FILLED} disabled:hover:brightness-100`,
  'danger-ghost': `bg-transparent text-danger hover:bg-danger-tint ${DISABLED_GHOST}`,
};

// 44 px minimum on the small size and 48 px on the large one: the client uses
// this between sets, one-handed, and the coach on a laptop.
const SIZES: Record<Size, string> = {
  md: 'min-h-11 px-4 text-sm',
  lg: 'min-h-12 px-5 text-base w-full',
};

/**
 * A button's clothes, without the button.
 *
 * Exported because one control in the product is genuinely a **link** wearing
 * them: "start the session" navigates, and a link is what navigates — it can be
 * opened in a new tab, it is announced as a link, and it works when JavaScript
 * has not arrived. Nesting a `<button>` inside an `<a>` to get the look is
 * invalid HTML and puts one destination in the tab order twice.
 *
 * So the styling is a function and the element is the caller's choice. There is
 * still exactly one place the variants are defined.
 */
export function buttonClassName({
  variant = 'primary',
  size = 'md',
  className,
}: {
  variant?: Variant;
  size?: Size;
  className?: string | undefined;
} = {}): string {
  return [
    'inline-flex items-center justify-center gap-2 rounded-[--radius-control]',
    // The disabled treatment is per-variant rather than shared, because a
    // filled control and a ghost one have different grounds to stay legible
    // against, and because nothing here should depend on which of two
    // same-specificity utilities Tailwind happens to emit last.
    'font-medium transition-colors disabled:cursor-not-allowed',
    VARIANTS[variant],
    SIZES[size],
    className ?? '',
  ].join(' ');
}

export function Button({ variant = 'primary', size = 'md', className, ...rest }: ButtonProps) {
  return (
    <button type="button" className={buttonClassName({ variant, size, className })} {...rest} />
  );
}
